import React, { useState, useEffect } from "react"
import moment from "moment"

import CatchModal from "~/library/CatchModal"
import { formatCurrency } from "~/utils/helpers"
import Booking from "~/types/Booking"
import PaymentItem from "~/types/PaymentItem"

import getAppConfig from "~/utils/getAppConfig"
import { apiPayments } from "~/routes"
import { useToast } from "~/utils/Toast"

interface PaymentModalProps {
  onClose: () => void
  onSuccess: () => void
  bookingId: string
  booking: Booking
}

const PaymentModal: React.FC<PaymentModalProps> = ({ onClose, onSuccess, bookingId, booking }) => {
  const toast = useToast()
  const { venueSlug } = getAppConfig()
  const [paymentItems, setPaymentItems] = useState<PaymentItem[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    const initialItems: PaymentItem[] = []

    if (booking?.bookingItems && booking.bookingItems.length > 0) {
      booking.bookingItems.forEach((bookingItem: any) => {
        initialItems.push({
          description: bookingItem.name,
          quantity: bookingItem.quantity,
          amountCents: bookingItem.amountCents || 0,
        })
      })
    }

    // If no booking items, add one empty item
    if (initialItems.length === 0) {
      initialItems.push({
        description: "",
        quantity: 1,
        amountCents: 0,
      })
    }

    setPaymentItems(initialItems)
  }, [booking])

  const handleCreateInvoice = () => {
    if (paymentItems.filter((item) => !item._destroy).length === 0) {
      toast("At least one payment item is required", "danger")
      return
    }

    setIsSubmitting(true)

    const req = apiPayments.create({
      data: {
        venueSlug,
        payableId: bookingId,
        payableType: "booking",
        payment: {
          amountCents: calculateTotal(),
          paymentItemsAttributes: paymentItems.filter((item) => !item._destroy),
        },
      },
    })
    req
      .then(() => {
        onSuccess()
        onClose()
      })
      .catch((error) => {
        console.error("Failed to create invoice:", error)
        toast(error.body.errors.join(", "), "danger")
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  const addPaymentItem = () => {
    setPaymentItems([
      ...paymentItems,
      {
        description: "",
        quantity: 1,
        amountCents: 0,
      },
    ])
  }

  const removePaymentItem = (index: number) => {
    const updatedItems = [...paymentItems]
    if (updatedItems[index].id) {
      updatedItems[index]._destroy = true
    } else {
      updatedItems.splice(index, 1)
    }
    setPaymentItems(updatedItems)
  }

  const updatePaymentItem = (index: number, field: keyof PaymentItem, value: any) => {
    const updatedItems = [...paymentItems]
    updatedItems[index] = { ...updatedItems[index], [field]: value }
    setPaymentItems(updatedItems)
  }

  const calculateTotal = () => {
    return paymentItems
      .filter((item) => !item._destroy)
      .reduce((total, item) => total + item.quantity * item.amountCents, 0)
  }

  const renderClientInfo = () => {
    const client = booking?.client
    if (!client) {
      return (
        <div className="alert alert-warning">No client information available for this booking.</div>
      )
    }

    return (
      <div className="row">
        <div className="col-12 mb-3">
          <h6 className="text-muted mb-2">BILL TO</h6>
          <div>
            <strong>{client.name}</strong>
            <br />
            {client.email && <span className="text-muted">{client.email}</span>}
            <br />
            {client.phone && <span className="text-muted">{client.phone}</span>}
          </div>
        </div>
      </div>
    )
  }

  const renderPaymentItems = () => {
    const visibleItems = paymentItems.filter((item) => !item._destroy)

    return (
      <div className="table-responsive">
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Description</th>
              <th>Qty</th>
              <th>Unit Price</th>
              <th>Subtotal</th>
              <th style={{ width: "50px" }}></th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.map((item) => {
              const actualIndex = paymentItems.indexOf(item)
              return (
                <tr key={actualIndex} className="align-middle">
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={item.description}
                      onChange={(e) =>
                        updatePaymentItem(actualIndex, "description", e.target.value)
                      }
                      placeholder="Down Payment for Booking #123"
                      required
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={item.quantity}
                      onChange={(e) =>
                        updatePaymentItem(actualIndex, "quantity", parseInt(e.target.value) || 1)
                      }
                      min="1"
                      style={{ width: "80px" }}
                      required
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={item.amountCents / 100}
                      onChange={(e) =>
                        updatePaymentItem(
                          actualIndex,
                          "amountCents",
                          Math.round((parseFloat(e.target.value) || 0) * 100)
                        )
                      }
                      step="1"
                      min="0"
                      style={{ width: "100px" }}
                      placeholder="0.00"
                      required
                    />
                  </td>
                  <td>
                    {formatCurrency({
                      cents: item.quantity * item.amountCents,
                      currency_iso: "USD",
                    })}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removePaymentItem(actualIndex)}
                      disabled={visibleItems.length === 1}
                      title="Remove item"
                    >
                      x
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3}>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={addPaymentItem}
                >
                  + Add Item
                </button>
              </td>
              <th>{formatCurrency({ cents: calculateTotal(), currency_iso: "USD" })}</th>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    )
  }

  return (
    <CatchModal isOpen={true} onClose={onClose} title="Create Invoice" size="lg">
      <div className="space-y-4">
        {/* Invoice Header */}
        <div className="row mb-4">
          <div className="col-6">
            <h6 className="text-muted mb-2">INVOICE FOR</h6>
            <div>
              <strong>{booking?.name}</strong>
              <br />
              <span className="text-muted">{moment(booking?.date).format("MMMM Do, YYYY")}</span>
              <br />
              {booking?.area && <span className="text-muted">{booking.area.name}</span>}
            </div>
          </div>
          <div className="col-6">{renderClientInfo()}</div>

          {/* Additional Details */}
          {booking?.details && (
            <div>
              <h6 className="text-muted mb-2">ADDITIONAL DETAILS</h6>
              <p className="text-muted">{booking.details}</p>
            </div>
          )}
        </div>

        {/* Invoice Items */}
        <div>
          <h6 className="text-muted mb-3">ITEMS TO INVOICE</h6>
          {renderPaymentItems()}
        </div>

        {/* Actions */}
        <div className="d-flex gap-2 mt-4">
          <button type="button" onClick={onClose} className="btn btn-secondary ms-auto">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCreateInvoice}
            className="btn btn-primary"
            disabled={
              isSubmitting ||
              !booking?.client ||
              paymentItems.filter(
                (item) => !item._destroy && item.description && item.amountCents > 0
              ).length === 0
            }
          >
            {isSubmitting ? "Creating..." : "Create Invoice"}
          </button>
        </div>
      </div>
    </CatchModal>
  )
}

export default PaymentModal
